import axios from 'axios';
import { authService } from './authService';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:42026';

// Tenant Salesforce External Client App registration (PUBLIC values only:
// the consumer key is a public client identifier, never a secret).
export interface PrivateMcpSalesforceApp {
  loginUrl: string;
  clientId: string;
  serverSlug?: string;
  updatedAt: number;
  updatedBy: string;
}

// Tenant Microsoft Entra app registration for the Microsoft 365 connector
// (PUBLIC values only: the app is a public client, PKCE, no secret exists).
export interface PrivateMcpM365App {
  tenantId: string;
  clientId: string;
  updatedAt: number;
  updatedBy: string;
}

// Tenant Google Cloud OAuth client for the Google Workspace connector.
// UNLIKE the two above this registration holds a confidential value: Google
// issues no public client for a hosted https callback, so a client secret is
// required. The secret is write-only from the portal's point of view: it is
// sent on save and never returned, so the UI shows `hasClientSecret` instead.
export interface PrivateMcpGoogleApp {
  clientId: string;
  workspaceDomain: string | null;
  hasClientSecret: boolean;
  updatedAt: number;
  updatedBy: string;
}

// Tenant HubSpot MCP connector (HubSpot: Development > MCP Connectors). Like
// Google, HubSpot's token endpoint requires the client secret, so the secret is
// write-only from the portal: sent on save, never returned (`hasClientSecret`).
// hubId optionally ties the connector to one HubSpot account.
export interface PrivateMcpHubSpotApp {
  clientId: string;
  hubId: string | null;
  hasClientSecret: boolean;
  updatedAt: number;
  updatedBy: string;
}

// Metadata row from the Private MCP connections vault. Tokens never leave
// the server; this is display metadata only.
export interface PrivateMcpGrant {
  userId: string;
  email: string | null;
  connectorId: string;
  instanceHost: string | null;
  updatedAt: number;
}

export interface PrivateMcpGrantList {
  grants: PrivateMcpGrant[];
  count: number;
  vaultReady: boolean;
}

class PrivateMcpService {
  private getAuthHeaders() {
    const token = authService.getToken();
    return token
      ? { 'x-access-token': token, 'x-pp-client-id': 'privacypal-user-portal' }
      : {};
  }

  // GET returns the registered app, or null when the server answers 404
  // not_configured (the expected empty state, not an error).
  async getSalesforceApp(): Promise<PrivateMcpSalesforceApp | null> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/private-mcp/salesforce/app`, {
        headers: this.getAuthHeaders(),
      });
      return response.data || null;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  }

  async setSalesforceApp(payload: { loginUrl: string; clientId: string; serverSlug?: string }): Promise<PrivateMcpSalesforceApp> {
    const response = await axios.put(`${API_BASE_URL}/api/private-mcp/salesforce/app`, payload, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async deleteSalesforceApp(): Promise<void> {
    await axios.delete(`${API_BASE_URL}/api/private-mcp/salesforce/app`, {
      headers: this.getAuthHeaders(),
    });
  }

  // Microsoft 365 (Entra app registration). Same empty-state convention.
  async getM365App(): Promise<PrivateMcpM365App | null> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/private-mcp/m365/app`, {
        headers: this.getAuthHeaders(),
      });
      return response.data || null;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  }

  async setM365App(payload: { tenantId: string; clientId: string }): Promise<PrivateMcpM365App> {
    const response = await axios.put(`${API_BASE_URL}/api/private-mcp/m365/app`, payload, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async deleteM365App(): Promise<void> {
    await axios.delete(`${API_BASE_URL}/api/private-mcp/m365/app`, {
      headers: this.getAuthHeaders(),
    });
  }

  // Google Workspace (Google Cloud OAuth client). Same empty-state convention.
  async getGoogleApp(): Promise<PrivateMcpGoogleApp | null> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/private-mcp/google/app`, {
        headers: this.getAuthHeaders(),
      });
      return response.data || null;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  }

  // clientSecret travels only in this direction. There is no endpoint that
  // returns it to a browser.
  async setGoogleApp(payload: { clientId: string; clientSecret: string; workspaceDomain?: string }): Promise<PrivateMcpGoogleApp> {
    const response = await axios.put(`${API_BASE_URL}/api/private-mcp/google/app`, payload, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async deleteGoogleApp(): Promise<void> {
    await axios.delete(`${API_BASE_URL}/api/private-mcp/google/app`, {
      headers: this.getAuthHeaders(),
    });
  }

  // HubSpot (MCP connector). Same empty-state convention.
  async getHubSpotApp(): Promise<PrivateMcpHubSpotApp | null> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/private-mcp/hubspot/app`, {
        headers: this.getAuthHeaders(),
      });
      return response.data || null;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  }

  // clientSecret travels only in this direction.
  async setHubSpotApp(payload: { clientId: string; clientSecret: string; hubId?: string }): Promise<PrivateMcpHubSpotApp> {
    const response = await axios.put(`${API_BASE_URL}/api/private-mcp/hubspot/app`, payload, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async deleteHubSpotApp(): Promise<void> {
    await axios.delete(`${API_BASE_URL}/api/private-mcp/hubspot/app`, {
      headers: this.getAuthHeaders(),
    });
  }

  // Admin-only: metadata listing of the Private MCP connections vault.
  async getGrants(): Promise<PrivateMcpGrantList> {
    const response = await axios.get(`${API_BASE_URL}/api/private-mcp/grants`, {
      headers: this.getAuthHeaders(),
    });
    return {
      grants: response.data?.grants || [],
      count: response.data?.count || 0,
      vaultReady: response.data?.vaultReady !== false,
    };
  }

  // Admin-only: revoke a member's stored grant for one connector.
  async revokeGrant(connectorId: string, userId: string): Promise<void> {
    await axios.delete(
      `${API_BASE_URL}/api/private-mcp/grants/${encodeURIComponent(connectorId)}/${encodeURIComponent(userId)}`,
      { headers: this.getAuthHeaders() },
    );
  }
}

export const privateMcpService = new PrivateMcpService();
